import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {Router} from '@angular/router';
import {AppointmentsController} from '../appointments/service/appointments.controller';
import {AppointmentsFlowStateService} from '../../common/service/appointments-flow-state.service';
import {ConsentState, PatientDetails} from '../appointments/model/appointments-models';
import {emailMatchValidator, germanNameValidator} from '../appointments/service/appointments-validators';
import {
  AppointmentSummaryCardComponent
} from '../appointments-shared/appointment-summary-card/appointment-summary-card.component';
import {ErrorBannerComponent} from '../appointments-shared/error-banner/error-banner.component';

@Component({
  selector: 'app-appointments-patient',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    AppointmentSummaryCardComponent,
    ErrorBannerComponent
  ],
  templateUrl: './appointments-patient.component.html',
  styleUrls: ['./appointments-patient.component.scss']
})
export class AppointmentsPatientComponent implements OnInit {
  isSubmitting = false;
  errorMessage: string | null = null;

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private controller: AppointmentsController,
    public flowState: AppointmentsFlowStateService,
    private router: Router
  ) {
    this.form = this.fb.group({
      bookingFor: ['self', Validators.required],
      firstName: ['', [Validators.required, germanNameValidator]],
      lastName: ['', [Validators.required, germanNameValidator]],
      birthDate: ['', Validators.required],
      phone: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      emailRepeat: ['', [Validators.required, Validators.email]],
      privacyAccepted: [false, Validators.requiredTrue],
      proxyAccepted: [false]
    }, {validators: emailMatchValidator});
  }

  ngOnInit(): void {
    const existing = this.flowState.snapshot.patientDetails;
    const consents = this.flowState.snapshot.consents;
    if (existing) {
      this.form.patchValue({
        bookingFor: existing.forOtherPerson ? 'other' : 'self',
        firstName: existing.firstName,
        lastName: existing.lastName,
        birthDate: existing.birthDate,
        phone: existing.phone,
        email: existing.email,
        emailRepeat: existing.email,
        privacyAccepted: consents?.privacyAccepted ?? false,
        proxyAccepted: consents?.proxyAccepted ?? false
      });
    }

    this.form.get('bookingFor')?.valueChanges.subscribe(value => {
      if (value === 'self') {
        this.form.get('proxyAccepted')?.setValue(false);
      }
    });
  }

  get isOtherPerson(): boolean {
    return this.form.get('bookingFor')?.value === 'other';
  }

  get emailMismatch(): boolean {
    return this.form.hasError('emailMismatch') && !!this.form.get('emailRepeat')?.touched;
  }

  submit() {
    if (this.form.invalid || (this.isOtherPerson && !this.form.get('proxyAccepted')?.value)) {
      this.form.markAllAsTouched();
      return;
    }

    const {preselection, selectedDay, selectedSlot} = this.flowState.snapshot;
    if (!preselection.insurance || !preselection.appointmentType || !preselection.patientStatus || !selectedDay || !selectedSlot) {
      this.errorMessage = 'Die Terminauswahl ist unvollstaendig. Bitte waehlen Sie den Termin erneut aus.';
      return;
    }

    const details: PatientDetails = {
      forOtherPerson: this.isOtherPerson,
      firstName: this.form.get('firstName')?.value ?? '',
      lastName: this.form.get('lastName')?.value ?? '',
      birthDate: this.form.get('birthDate')?.value ?? '',
      phone: this.form.get('phone')?.value ?? '',
      email: this.form.get('email')?.value ?? ''
    };
    const consents: ConsentState = {
      privacyAccepted: !!this.form.get('privacyAccepted')?.value,
      proxyAccepted: this.isOtherPerson ? !!this.form.get('proxyAccepted')?.value : false
    };

    this.isSubmitting = true;
    this.errorMessage = null;

    this.controller.submitPatientDetails(preselection, selectedDay, selectedSlot, details, consents).subscribe({
      next: () => {
        this.flowState.updatePatientDetails(details, consents);
        this.isSubmitting = false;
        this.router.navigate(['/verifizierung']);
      },
      error: () => {
        this.isSubmitting = false;
        this.errorMessage = 'Die Daten konnten nicht gespeichert werden. Bitte prüfen Sie die Eingaben.';
      }
    });
  }

  back() {
    this.router.navigate(['/kalender']);
  }
}
